import { Check, Info } from 'lucide-react';

const weeklyPrices = [
  {
    apartment: 'Muscat (N\u00B02)',
    subtitle: 'Duplex \u2022 env. 75 m\u00B2 \u2022 2 a 6 pers.',
    lowSeason: '519',
    highSeason: '599',
    id: 'muscat'
  },
  {
    apartment: 'Riesling (N\u00B04)',
    subtitle: 'Appartement \u2022 env. 65 m\u00B2 \u2022 2 a 5 pers.',
    lowSeason: '469',
    highSeason: '549',
    id: 'riesling'
  }
];

const nightlyPrices = [
  { apartment: 'Muscat (N\u00B02)', nightLow: '49', nightHigh: '59', weekendLow: '79', weekendHigh: '89', id: 'muscat' },
  { apartment: 'Riesling (N\u00B04)', nightLow: '39', nightHigh: '49', weekendLow: '69', weekendHigh: '79', id: 'riesling' }
];

const includes = [
  'Charges, eau, electricite, chauffage',
  'Taxe de sejour incluse',
  'Enlevement des ordures menageres',
  'WiFi gratuit',
];

export default function PricingSection() {
  return (
    <section
      id="tarifs"
      data-testid="pricing-section"
      className="py-24 md:py-32 bg-white"
    >
      <div className="container mx-auto px-4 md:px-8 max-w-6xl">
        <div className="text-center mb-16 md:mb-20">
          <p className="font-accent text-wine-700 text-lg tracking-[0.2em] uppercase mb-4">
            Nos tarifs
          </p>
          <h2 className="font-display text-4xl md:text-5xl text-earth-100 mb-6">
            Des prix tout compris
          </h2>
          <p className="font-body text-earth-200 text-lg max-w-xl mx-auto leading-relaxed">
            Profitez de tarifs attractifs, bien moins chers que l'hotel,
            avec tout le confort d'un appartement
          </p>
        </div>

        {/* Weekly prices */}
        <div className="mb-16">
          <h3 className="font-display text-2xl text-earth-100 mb-8 text-center">
            Tarifs a la semaine
          </h3>
          <div className="grid md:grid-cols-2 gap-6">
            {weeklyPrices.map((p) => (
              <div
                key={p.id}
                data-testid={`weekly-price-${p.id}`}
                className="border border-cream-400 p-8 bg-cream-50 hover:shadow-md transition-shadow duration-300"
              >
                <h4 className="font-display text-2xl text-earth-100 mb-1">{p.apartment}</h4>
                <p className="font-body text-sm text-earth-300 mb-8">{p.subtitle}</p>
                <div className="grid grid-cols-2 gap-6">
                  <div className="text-center p-4 bg-white border border-cream-400">
                    <p className="font-body text-xs uppercase tracking-[0.15em] text-earth-300 mb-2">Basse saison</p>
                    <p className="font-display text-3xl text-wine-700">{p.lowSeason}&euro;</p>
                    <p className="font-body text-xs text-earth-300 mt-1">/ semaine</p>
                  </div>
                  <div className="text-center p-4 bg-wine-700 text-white">
                    <p className="font-body text-xs uppercase tracking-[0.15em] text-white/70 mb-2">Haute saison</p>
                    <p className="font-display text-3xl">{p.highSeason}&euro;</p>
                    <p className="font-body text-xs text-white/70 mt-1">/ semaine</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Nightly prices */}
        <div className="mb-16">
          <h3 className="font-display text-2xl text-earth-100 mb-8 text-center">
            Tarifs a la nuitee et au week-end
          </h3>
          <div className="overflow-x-auto">
            <table data-testid="nightly-prices-table" className="w-full border-collapse">
              <thead>
                <tr className="border-b-2 border-cream-400">
                  <th className="text-left py-4 px-4 font-display text-lg text-earth-100">Appartement</th>
                  <th className="text-center py-4 px-3 font-body text-xs uppercase tracking-wider text-earth-300">
                    Nuitee<br/>basse saison
                  </th>
                  <th className="text-center py-4 px-3 font-body text-xs uppercase tracking-wider text-earth-300">
                    Nuitee<br/>haute saison
                  </th>
                  <th className="text-center py-4 px-3 font-body text-xs uppercase tracking-wider text-earth-300">
                    Week-end<br/>basse saison
                  </th>
                  <th className="text-center py-4 px-3 font-body text-xs uppercase tracking-wider text-earth-300">
                    Week-end<br/>haute saison
                  </th>
                </tr>
              </thead>
              <tbody>
                {nightlyPrices.map((p) => (
                  <tr key={p.id} className="border-b border-cream-400 hover:bg-cream-50 transition-colors">
                    <td className="py-5 px-4 font-display text-lg text-earth-100">{p.apartment}</td>
                    <td className="text-center py-5 px-3 font-display text-xl text-earth-100">{p.nightLow}&euro;</td>
                    <td className="text-center py-5 px-3 font-display text-xl text-wine-700 font-semibold">{p.nightHigh}&euro;</td>
                    <td className="text-center py-5 px-3 font-display text-xl text-earth-100">{p.weekendLow}&euro;</td>
                    <td className="text-center py-5 px-3 font-display text-xl text-wine-700 font-semibold">{p.weekendHigh}&euro;</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="font-body text-xs text-earth-300 mt-2 text-center italic">Prix par personne</p>
          </div>
        </div>

        {/* Includes + extras */}
        <div className="grid md:grid-cols-2 gap-8">
          <div className="p-8 bg-cream-50 border border-cream-400">
            <h4 className="font-display text-xl text-earth-100 mb-6">Ces tarifs comprennent</h4>
            <ul className="space-y-3">
              {includes.map((item) => (
                <li key={item} className="flex items-center gap-3 font-body text-sm text-earth-200">
                  <Check size={16} className="text-sage-400 flex-shrink-0" />
                  {item}
                </li>
              ))}
            </ul>
          </div>

          <div className="p-8 bg-cream-50 border border-cream-400">
            <h4 className="font-display text-xl text-earth-100 mb-6">Informations pratiques</h4>
            <div className="space-y-4 font-body text-sm text-earth-200">
              <div className="flex items-start gap-3">
                <Info size={16} className="text-wine-700 mt-0.5 flex-shrink-0" />
                <p><strong>Enfants &lt; 5 ans :</strong> Gratuit *</p>
              </div>
              <div className="flex items-start gap-3">
                <Info size={16} className="text-wine-700 mt-0.5 flex-shrink-0" />
                <p><strong>Enfants &lt; 18 ans :</strong> 50% du prix *</p>
              </div>
              <div className="flex items-start gap-3">
                <Info size={16} className="text-wine-700 mt-0.5 flex-shrink-0" />
                <p><strong>Forfait menage :</strong> 40-50&euro; (optionnel)</p>
              </div>
              <div className="flex items-start gap-3">
                <Info size={16} className="text-wine-700 mt-0.5 flex-shrink-0" />
                <p><strong>Linges :</strong> 18&euro;/personne (optionnel)</p>
              </div>
              <p className="text-xs text-earth-300 italic mt-4">
                * Sous condition d'etre accompagne d'au moins 2 adultes
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
